"use client";
import { useMemo } from "react";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";
// import "swiper/css";
// import "swiper/css/navigation";
import { ChevronLeft, ChevronRight } from "lucide-react";
import UNLOX from "@/assets/logo/unlox.png";
import Nephro from "@/assets/logo/nephro.png";
import Title from "../Title";

import Student1 from "@/assets/students/anil-sharma.png";
import Student2 from "@/assets/students/doma-tamang.png";
import Student3 from "@/assets/students/heema.png";
import Student4 from "@/assets/students/kristina-thatal.png";

// Media URL helper for Strapi
const getMediaUrl = (media) => {
  const baseUrl = process.env.NEXT_PUBLIC_STRAPI_URL || "http://localhost:1337";
  const url = media?.url || media?.data?.attributes?.url || media?.data?.url;
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${baseUrl}${url}`;
};

// Rich Text helper for Strapi Blocks
const renderRichText = (blocks) => {
  if (typeof blocks === 'string') return blocks;
  if (Array.isArray(blocks)) {
    return blocks.map(block => {
      if (block.type === 'paragraph') {
        return block.children.map(child => child.text).join('');
      }
      return '';
    }).join('\n');
  }
  return '';
};

const defaultStudents = [
  {
    name: "Anil Sharma",
    title: "Business Development Associate",
    companyLogo: UNLOX,
    image: Student1,
  },
  {
    name: "Doma Tamang",
    title: "Junior Dialysis Therapist",
    companyLogo: Nephro,
    image: Student2,
  },
  {
    name: "Heema",
    title: "Business Development Associate",
    companyLogo: UNLOX,
    image: Student3,
  },
  {
    name: "Kristina Thatal",
    title: "Junior Dialysis Therapist",
    companyLogo: Nephro,
    image: Student4,
  },
  {
    name: "Anil Sharma",
    title: "Business Development Associate",
    companyLogo: UNLOX,
    image: Student1,
  },
  {
    name: "Doma Tamang",
    title: "Junior Dialysis Therapist",
    companyLogo: Nephro,
    image: Student2,
  },
  {
    name: "Heema",
    title: "Business Development Associate",
    companyLogo: UNLOX,
    image: Student3,
  },
  {
    name: "Kristina Thatal",
    title: "Junior Dialysis Therapist",
    companyLogo: Nephro,
    image: Student4,
  },
];

const FromCampusToCareer = ({ data, loading }) => {
  // Map Strapi Placements_block data to display format
  const dynamicStudents = useMemo(() => {
    const blocks = data?.Placements_block;
    if (!blocks || !Array.isArray(blocks) || blocks.length === 0) return [];
    return blocks.map((block) => ({
      name: block.Student_name || "",
      title: block.Student_designation || "",
      image: getMediaUrl(block.Image) || "",
      companyLogo: getMediaUrl(block.Placement_logo) || "",
      isDynamic: true,
    }));
  }, [data]);

  const students = dynamicStudents.length > 0 ? dynamicStudents : defaultStudents;
  const isDynamic = dynamicStudents.length > 0;

  // Section title and description
  const sectionTitle = data?.Title || "From Campus to Thriving Careers";
  const sectionDescription = renderRichText(data?.Description) || "MSU students who excelled have secured strong placements with skills, confidence, and hands-on experience through structured OJL and pathways across leading organisations.";

  return (
    <section className="page-container w-full mx-auto bg-cover bg-center bg-no-repeat mt-[20px] md:mt-[50px]">
      <div className="relative mb-10 md:mb-15 lg:mb-25 w-full">
        <div className="">
          <Title
            heading={sectionTitle}
            subText={sectionDescription}
            color="text-black"
            subTextColor="text-black/70"
            align="text-left"
          />
          <div className="flex justify-end gap-3 mb-6">
            <button
              id="prevButton"
              className="cursor-pointer bg-white border border-gray-300 rounded-full p-2 shadow hover:bg-gray-100 transition"
              aria-label="Previous"
            >
              <ChevronLeft className="w-5 h-5 text-gray-700" />
            </button>
            <button
              id="nextButton"
              className="cursor-pointer bg-white border border-gray-300 rounded-full p-2 shadow hover:bg-gray-100 transition"
              aria-label="Next"
            >
              <ChevronRight className="w-5 h-5 text-gray-700" />
            </button>
          </div>
          <Swiper
            modules={[Navigation, Autoplay]}
            spaceBetween={16}
            loop={true}
            slidesPerView={1.3}
            autoplay={{
              delay: 3000,
              disableOnInteraction: false,
            }}
            navigation={{
              prevEl: "#prevButton",
              nextEl: "#nextButton",
            }}
            breakpoints={{
              640: { slidesPerView: 1.3 },
              768: { slidesPerView: 2.3 },
              1024: { slidesPerView: 3.3 },
              1280: { slidesPerView: 4 },
            }}
            className="career-swiper w-full"
          >
            {students.map(({ name, title, companyLogo, image }, index) => (
              <SwiperSlide
                key={index}
                className="h-auto flex flex-col bg-white"
              >
                <div className="flex flex-col h-full w-full max-w-[420px] mx-auto">
                  <div className="relative w-full aspect-4/5 overflow-hidden shrink-0">
                    {isDynamic ? (
                      <img
                        src={image}
                        alt={name}
                        className="object-cover w-full h-full absolute inset-0"
                        loading="lazy"
                      />
                    ) : (
                      <Image
                        src={image}
                        alt={name}
                        fill
                        className="object-cover w-full h-full"
                        loading="lazy"
                      />
                    )}
                    <div
                      className="absolute bottom-0 left-0 right-0 h-full w-full flex flex-col justify-end px-4 py-4 text-left"
                      style={{
                        background:
                          "linear-gradient(180deg, rgba(8, 30, 45, 0) 45.54%, rgba(8, 30, 45, 0.8) 91.22%)",
                      }}
                    >
                      <p className="font-bold text-white text-[18px]">
                        {name}
                      </p>
                      <p className="text-white text-xs text-[15px]">{title}</p>
                    </div>
                  </div>

                  <div className="w-full flex justify-center items-center py-6 border border-[rgba(205,226,235,1)] flex-1">
                    {isDynamic ? (
                      <img
                        src={companyLogo}
                        alt={`${name} company logo`}
                        className="h-[20px] md:h-[30px] w-auto"
                      />
                    ) : (
                      <Image
                        src={companyLogo}
                        alt={`${name} company logo`}
                        width={90}
                        height={60}
                        className="h-[20px] md:h-[30px] w-auto"
                      />
                    )}
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </section>
  );
};

export default FromCampusToCareer;
